import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  FileText,
  Hammer,
  Instagram,
  Mail,
  MapPin,
  Menu,
  Phone,
  Play,
  Send,
  ShieldCheck,
  Sun,
  X,
  Zap
} from "lucide-react";
import axios from "axios";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO =
"https://images.unsplash.com/photo-1543489816-c87b0f5f7dd4?auto=format&fit=crop&w=2200&q=85";
const PROJECT_IMAGE = "/posters/panel-room.jpeg";
const CRANE_VIDEO = "/posters/crane-erection.jpeg";
const IMG_BASE = "/posters";

const SERVICE_IMAGES = [
  // 01 — SUBSTATION & BAY CONSTRUCTION
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/91839781fd659c03c9517af509bbe5b8e846ad8f431dbca23e11646d8ef11f46.jpeg",

  // 02 — HT/LT INFRASTRUCTURE
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/e76929e2b3178ce2a71a25f8bc6434795102f40e64633d0e46893cb4ad0c7069.jpeg",

  // 03 — HEAVY EQUIPMENT CARRIAGE & HANDLING
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/3f25347acda5b232442ce5cb3594468cf36c32338f66307dc73bb16e4ab16573.jpeg",

  // 04 — PROTECTION, CONTROL PANELS & EARTHING
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/c622b50259edb57f4b639ad118171160c3b7d2cf8d576d4f537b9246689363b4.jpeg",

  // 05 — TOWER, STRUCTURAL & FABRICATION WORKS
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/86b34ace8ff4692bef96842b1acf54e80dfaf7b1a305d9b3c8a32d2ef5dce551.jpeg",

  // 06 — TRANSFORMER & SWITCHGEAR SERVICES
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/40fa01f34819c8803afbd917ac7c083d6fb25213a57e777b145aa1f5074fa80b.jpeg",

  // 07 — SOLAR & LIGHTING SOLUTIONS
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/c2b83d61a64329b47683bead4abeadfc2054ddce9289ddf255fe01fca9c919b1.jpeg",

  // 08 — ELECTRICAL MAINTENANCE & EMERGENCY SERVICES
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/98c276b29433327b215bf56442a6c55c49b04b0bbd6a742a38986ef5d6994f47.jpeg",

  // 09 — ELECTRICAL SUPPLY, PROCUREMENT & TENDER EXECUTION
  "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/bac4d731d52d8f7f03e7a2362e2f4c93ae25c440dc4898007f7e9961cdd5b201.jpeg"
];

const services = [
  [
    "01",
    "SUBSTATION & BAY CONSTRUCTION",
    "33kV / 132kV / 220kV Substation • Bay Construction • Equipment Erection • Installation • Commissioning",
    "Substation works, bay construction, equipment erection, installation and commissioning for demanding power infrastructure.",
    SERVICE_IMAGES[0]
  ],
  [
    "02",
    "HT/LT INFRASTRUCTURE",
    "HT/LT Electrical Works • Cabling • Distribution Systems • Installation • Maintenance",
    "HT/LT electrical works, cabling, distribution systems, installation and maintenance.",
    SERVICE_IMAGES[1]
  ],
  [
    "03",
    "HEAVY EQUIPMENT CARRIAGE & HANDLING",
    "Transformer • CT/PT • Circuit Breaker • Loading • Carriage • Unloading • Hydra/Crane Handling",
    "Safe loading, carriage, unloading, shifting and handling of heavy electrical equipment.",
    SERVICE_IMAGES[2]
  ],
  [
    "04",
    "PROTECTION, CONTROL PANELS & EARTHING",
    "Control & Relay Panels • Protection Systems • Cable Termination • Interconnection • Equipment Earthing",
    "Protection systems, panels, cable termination, interconnection and specialized earthing.",
    SERVICE_IMAGES[3]
  ],
  [
    "05",
    "TOWER, STRUCTURAL & FABRICATION WORKS",
    "Tower Works • Equipment Structures • Fabrication • Welding • Structural Installation • Foundation Modification",
    "Tower, structural, welding, fabrication and foundation modification works.",
    SERVICE_IMAGES[4]
  ],
  [
    "06",
    "TRANSFORMER & SWITCHGEAR SERVICES",
    "Transformer Installation • Switchgear • Circuit Breakers • Dismantling • Erection • Testing",
    "Transformer and switchgear installation, dismantling, erection and testing.",
    SERVICE_IMAGES[5]
  ],
  [
    "07",
    "SOLAR & LIGHTING SOLUTIONS",
    "Solar Projects • Solar Installation • LED Lighting • Street Lighting • Electrical Lighting Solutions",
    "Solar projects, LED lighting, street lighting and electrical lighting solutions.",
    SERVICE_IMAGES[6]
  ],
  [
    "08",
    "ELECTRICAL MAINTENANCE & EMERGENCY SERVICES",
    "Breakdown Support • Equipment Replacement • Testing • Repair • Preventive Maintenance • AC Maintenance",
    "Breakdown support, equipment replacement, testing, repair, preventive and AC maintenance.",
    SERVICE_IMAGES[7]
  ],
  [
    "09",
    "ELECTRICAL SUPPLY, PROCUREMENT & TENDER EXECUTION",
    "Electrical Equipment • Cables • Switchgear • Lighting & Earthing Materials • Solar Equipment • Tender Execution",
    "Supply and procurement of electrical equipment with end-to-end government and private tender execution.",
    SERVICE_IMAGES[8]
  ]
];

const credentials = [
  [
    "A-Class Government Contractor",
    "Registered & Certified",
    "Registered A-Class electrical contractor, qualified to execute government electrical and power infrastructure works."
  ],
  [
    "Government & Private Tender Execution",
    "End-to-End Delivery",
    "Complete participation and execution of government and private tenders — from bidding through commissioning."
  ],
  [
    "UPPTCL / Power Transmission Works",
    "Transmission Experience",
    "Execution experience on Uttar Pradesh Power Transmission Corporation Ltd. transmission and substation works."
  ],
  [
    "Adani Solar Vendor",
    "Registered Vendor",
    "Registered vendor for Adani solar projects, supporting installation and allied electrical works."
  ],
  [
    "Electrical Equipment Supply & Procurement",
    "Trusted Supplier",
    "Dependable supply and procurement of electrical equipment, cables, switchgear and allied materials."
  ]
];

function CredentialsStrip() {
  return (
    <section className="credentials-strip" data-testid="credentials-strip">
      {credentials.map(([title, tag], i) => (
        <div className="credential-item" key={title}>
          <span className="credential-index">0{i + 1}</span>
          <div>
            <strong>{title}</strong>
            <em>{tag}</em>
          </div>
        </div>
      ))}
    </section>
  );
}

function CredentialsPanel() {
  return (
    <div className="credentials-section">
      <SectionIntro
        eyebrow="CREDENTIALS & RECOGNITION"
        title={
          <>
            Qualified where
            <br />
            <em>it matters.</em>
          </>
        }
        copy="Registrations, empanelments and working relationships that stand behind every scope of work we take on."
      />

      <div className="credential-list">
        {credentials.map(([title, tag, detail], i) => (
          <div key={title}>
            <ShieldCheck size={19} />
            <span className="num">
              0{i + 1} / {tag.toUpperCase()}
            </span>
            <h3>{title}</h3>
            <p>{detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const categories = services.map(([, title]) => title);

const serviceSlug = (title) =>
  title
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const contact = {
  phone: "991812121026",
  phone2: "8299351624",
  email: "maavaishnocons.supplier@gmail.com",
  address: "1092 F Block Barra 8 Kanpur"
};

const SEO_META = {
  "/": [
    "Electrical Contractor in Kanpur | A-Class Government Contractor — Maa Vaishno Construction & Supplier",
    "A-Class government electrical contractor in Kanpur, UP. Substation & bay construction, UPPTCL power transmission works, HT/LT, solar, tender execution and electrical equipment supply."
  ],
  "/about": [
    "About Us | Maa Vaishno Construction & Supplier — Electrical Contractor Kanpur",
    "A-Class government contractor led by Rajesh Kumar Awasthi. UPPTCL transmission works, Adani solar vendor, government & private tender execution across Uttar Pradesh."
  ],
  "/services": [
    "Electrical Contracting Services in Kanpur | Substation, HT/LT, Solar, Supply",
    "Nine electrical capabilities: substation & bay construction, HT/LT infrastructure, transformer & switchgear, solar & lighting, maintenance and equipment supply in Kanpur, UP."
  ],
  "/projects": [
    "Our Work & Experience | Maa Vaishno Construction & Supplier",
    "Electrical execution experience — substations, UPPTCL transmission works, solar projects and equipment supply by an A-Class government contractor in Kanpur."
  ],
  "/gallery": [
    "Site Photos & Videos | Maa Vaishno Construction & Supplier Kanpur",
    "Real photos and videos from our substation, switchyard, panel, solar and supply assignments across Uttar Pradesh."
  ],
  "/quote": [
    "Get a Quote | Electrical Contractor Kanpur — Maa Vaishno",
    "Request a quote for substation works, HT/LT, transformer erection, solar, maintenance or electrical equipment supply in Kanpur and Uttar Pradesh."
  ],
  "/tender": [
    "Tender Inquiry | Government & Private Tender Execution — Maa Vaishno",
    "Partner with an A-Class government contractor for tender participation and execution — UPPTCL, government and private electrical tenders."
  ],
  "/contact": [
    "Contact Us | Maa Vaishno Construction & Supplier, Kanpur",
    "Contact our Kanpur office: +91 8299351624, maavaishnocons.supplier@gmail.com, 1092 F Block Barra 8, Kanpur, Uttar Pradesh."
  ]
};

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    let meta = SEO_META[location.pathname];

    if (!meta && location.pathname.startsWith("/services/")) {
      const slug = location.pathname.split("/")[2];
      const match = services.find(
        ([, title]) => serviceSlug(title) === slug
      );

      if (match) {
        meta = [
          `${match[1]} in Kanpur | Maa Vaishno Construction & Supplier`,
          `${match[3]} A-Class government electrical contractor serving Kanpur and Uttar Pradesh.`
        ];
      }
    }

    const [title, description] = meta || SEO_META["/"];

    document.title = title;

    const tag = document.querySelector('meta[name="description"]');

    if (tag) {
      tag.setAttribute("content", description);
    }

    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    ["Home", "/"],
    ["About", "/about"],
    ["Services", "/services"],
    ["Our Work", "/projects"],
    ["Gallery", "/gallery"],
    ["Contact", "/contact"]
  ];

  return (
    <header className="site-header">
      <Link className="brand" to="/">
        <span className="brand-mark">
          <Zap size={19} fill="currentColor" />
        </span>

        <span>
          MAA VAISHNO
          <strong>CONSTRUCTION & SUPPLIER</strong>
        </span>
      </Link>

      <button
        className="menu-btn"
        onClick={() => setOpen(!open)}
        aria-label="Toggle navigation"
      >
        {open ? <X /> : <Menu />}
      </button>

      <nav className={open ? "nav open" : "nav"}>
        {links.map(([name, href]) => (
          <Link
            key={href}
            className={location.pathname === href ? "active" : ""}
            to={href}
            onClick={() => setOpen(false)}
          >
            {name}
          </Link>
        ))}

        <Link className="nav-cta" to="/quote">
          Get a Quote <ArrowUpRight size={15} />
        </Link>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <Link className="brand" to="/">
          <span className="brand-mark">
            <Zap size={19} fill="currentColor" />
          </span>

          <span>
            MAA VAISHNO
            <strong>CONSTRUCTION & SUPPLIER</strong>
          </span>
        </Link>

        <p className="footer-copy">
          Reliable electrical execution for the infrastructure that keeps
          industry moving.
        </p>
      </div>

      <div>
        <p className="footer-label">QUICK LINKS</p>

        <div className="footer-links">
          <Link to="/about">About</Link>
          <Link to="/services">Services</Link>
          <Link to="/projects">Our Work</Link>
          <Link to="/gallery">Gallery</Link>
          <Link to="/quote">Get a Quote</Link>
          <Link to="/tender">Tender Inquiry</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </div>

      <div>
        <p className="footer-label">CONTACT</p>

        <a href={`tel:${contact.phone}`}>+91 {contact.phone}</a>
        <a href={`mailto:${contact.email}`}>{contact.email}</a>
        <span>{contact.address}</span>
      </div>

      <div className="footer-bottom">
        © {new Date().getFullYear()} M/S MAA VAISHNO CONSTRUCTION AND
        SUPPLIER
        <span>Built for dependable execution.</span>
      </div>
    </footer>
  );
}

function SectionIntro({ eyebrow, title, copy }) {
  return (
    <div className="section-intro">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

function Home() {
  return (
    <>
      <section
        className="hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(2,12,27,.96) 0%, rgba(2,12,27,.76) 47%, rgba(2,12,27,.2) 100%), url(${HERO})`
        }}
      >
        <div className="hero-content">
          <span className="eyebrow gold">
            ELECTRICAL & POWER INFRASTRUCTURE CONTRACTOR
          </span>

          <span className="hero-badge">
            <ShieldCheck size={15} /> A-Class Government Contractor
          </span>

          <h1>
            Powering infrastructure
            <br />
            <em>through reliable</em>
            <br />
            electrical execution.
          </h1>

          <p>
            Heavy electrical works, HT/LT infrastructure, substations, solar
            projects and equipment installation — delivered with precision.
          </p>

          <div className="hero-actions">
            <Link to="/services" className="button gold-button">
              Explore Our Services <ArrowUpRight size={17} />
            </Link>

            <Link to="/quote" className="text-button">
              Get a Quote <ChevronRight size={17} />
            </Link>
          </div>
        </div>

        <div className="hero-foot">
          <span>Serving infrastructure with discipline</span>
          <span className="hero-rule" />
          <span>Kanpur, Uttar Pradesh</span>
        </div>
      </section>

      <CredentialsStrip />

      <section className="trust-strip">
        <span>
          <ShieldCheck size={18} /> Execution-led expertise
        </span>

        <span>
          <Hammer size={18} /> Heavy electrical capability
        </span>

        <span>
          <Sun size={18} /> Solar & lighting solutions
        </span>

        <span>
          <Zap size={18} /> Supply & procurement
        </span>
      </section>

      <section className="about-band">
<div
  className="about-image"
  style={{
    backgroundImage:
      'url("https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/91839781fd659c03c9517af509bbe5b8e846ad8f431dbca23e11646d8ef11f46.jpeg")',
  }}
>
  <span className="about-image-label">FIELD NOTE / 01</span>
</div>

        <div>
          <SectionIntro
            eyebrow="01 / WHO WE ARE"
            title={
              <>
                Infrastructure demands
                <br />
                <em>quiet confidence.</em>
              </>
            }
            copy="M/S Maa Vaishno Construction and Supplier is an electrical contracting and equipment supply partner for demanding industrial power environments."
          />

          <p className="body-copy">
            From substation execution and equipment erection to maintenance,
            solar, lighting and procurement, we bring practical site capability
            to every phase of an electrical project.
          </p>

          <Link className="arrow-link" to="/about">
            Discover our approach <ArrowUpRight size={17} />
          </Link>
        </div>
      </section>

      <section className="capabilities">
        <SectionIntro
          eyebrow="02 / WHAT WE DO"
          title={
            <>
              The work behind
              <br />
              <em>reliable power.</em>
            </>
          }
          copy="Nine connected capabilities for the electrical infrastructure your project depends on."
        />

        <ServiceGrid compact />
      </section>

      <VideoHighlight />

      <ProjectPreview />

      <FieldGallery />

      <Testimonials />
    </>
  );
}

const testimonials = [
  [
    "The transformer erection and commissioning was handled with complete discipline. Crane work, alignment and testing were managed without a single safety incident on our switchyard.",
    "Project Engineer",
    "132kV Substation Works, Uttar Pradesh"
  ],
  [
    "From tender documentation to final handover, everything moved on schedule. This is a contractor who understands how government electrical projects actually run on the ground.",
    "Contracts Manager",
    "Government Tender Client"
  ],
  [
    "We procured solar street lights and earthing materials — genuine products, fair rates and delivery exactly on the committed dates. Our go-to supplier now.",
    "Purchase Head",
    "Industrial Supply Client, Kanpur"
  ]
];

function Testimonials() {
  return (
    <section className="testimonials">
      <SectionIntro
        eyebrow="06 / CLIENT WORDS"
        title={
          <>
            Trusted on site.
            <br />
            <em>Proven on paper.</em>
          </>
        }
        copy="What the people we work with say about our execution, supply and support."
      />

      <div className="testimonial-grid">
        {testimonials.map(([quote, name, role]) => (
          <figure key={role}>
            <span className="quote-mark">“</span>
            <blockquote>{quote}</blockquote>

            <figcaption>
              <strong>{name}</strong>
              <span>{role}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function VideoHighlight() {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="video-highlight">
      <div className="video-highlight-inner">
        <div>
          <SectionIntro
            eyebrow="03 / SEE US AT WORK"
            title={
              <>
                Watch a 220kV breaker
                <br />
                <em>column go up.</em>
              </>
            }
            copy="Filmed on our own switchyard assignment — crane erection, rigging and precision heavy equipment handling by the Maa Vaishno team."
          />

          <Link className="arrow-link" to="/gallery">
            More site videos in the gallery <ArrowUpRight size={17} />
          </Link>
        </div>

        {playing && CRANE_VIDEO ? (
          <video
            className="highlight-video"
            src={CRANE_VIDEO}
            poster="/posters/crane-erection.jpeg"
            controls
            autoPlay
            playsInline
          />
        ) : (
          <button
            type="button"
            className="highlight-poster"
            onClick={() => {
              if (CRANE_VIDEO) setPlaying(true);
            }}
          >
            <div
              style={{
                backgroundImage: "url(/posters/crane-erection.jpeg)"
              }}
            />

            {CRANE_VIDEO && (
              <span className="play-badge">
                <Play size={26} fill="currentColor" />
              </span>
            )}

            <em>CRANE ERECTION AT A LIVE SWITCHYARD</em>
          </button>
        )}
      </div>
    </section>
  );
}

function ServiceGrid({ compact = false }) {
  const serviceLinks = {
    "SUBSTATION & BAY CONSTRUCTION":
      "/services/substation-bay-construction",

    "HT/LT INFRASTRUCTURE":
      "/services/ht-lt-infrastructure",

    "HEAVY EQUIPMENT CARRIAGE & HANDLING":
      "/services/heavy-equipment-carriage-handling",

    "PROTECTION, CONTROL PANELS & EARTHING":
      "/services/protection-control-panels-earthing",

    "TOWER, STRUCTURAL & FABRICATION WORKS":
      "/services/tower-structural-fabrication-works",

    "TRANSFORMER & SWITCHGEAR SERVICES":
      "/services/transformer-switchgear-services",

    "SOLAR & LIGHTING SOLUTIONS":
      "/services/solar-lighting-solutions",

    "ELECTRICAL MAINTENANCE & EMERGENCY SERVICES":
      "/services/electrical-maintenance-emergency-services",

    "ELECTRICAL SUPPLY, PROCUREMENT & TENDER EXECUTION":
      "/services/electrical-supply-procurement-tender-execution"
  };

  return (
    <div className="service-grid">
      {services.map(
        ([number, title, line, description, image]) => (
          <Link
            to={
              serviceLinks[title] ||
              `/services/${serviceSlug(title)}`
            }
            className="service-card"
            key={number}
          >
            <div className="service-card-media">
              <div
                className="service-card-img"
                style={{
                  backgroundImage: `url("${image}")`
                }}
              />

              <span className="service-number">
                {number}
              </span>
            </div>

            <div className="service-card-content">
              <h3>{title}</h3>

              <strong>{line}</strong>

              <p>{description}</p>

              <span className="view-details">
                View Details
                <ArrowUpRight size={15} />
              </span>
            </div>
          </Link>
        )
      )}
    </div>
  );
}

function ProjectPreview() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((r) => setProjects(r.data.data || []))
      .catch(() => setProjects([]));
  }, []);

  return (
    <section className="projects-preview">
      <div className="project-preview-heading">
        <SectionIntro
          eyebrow="04 / OUR WORK"
          title={
            <>
              Experience is built
              <br />
              <em>on site.</em>
            </>
          }
          copy="A growing record of electrical execution, equipment handling and infrastructure support."
        />

        <Link className="arrow-link" to="/projects">
          View all experience <ArrowUpRight size={17} />
        </Link>
      </div>

      {projects.length ? (
        <div className="project-list">
          {projects.slice(0, 3).map((p) => (
            <Link to={`/projects/${p.slug}`} key={p.slug}>
              <span>{p.category}</span>
              <h3>{p.title}</h3>
              <ArrowUpRight size={18} />
            </Link>
          ))}
        </div>
      ) : (
        <div className="empty-projects">
          <FileText size={21} />

          <span>
            <strong style={{ color: "#fff" }}>
              10+ years of electrical execution experience.
            </strong>{" "}
            Detailed project records are being added soon.
          </span>

          <Link to="/contact">
            Talk to us about your work <ArrowUpRight size={15} />
          </Link>
        </div>
      )}
    </section>
  );
}

function PageShell({ children, title, eyebrow }) {
  return (
    <>
      <div className="page-banner">
        <div>
          <span className="eyebrow gold">{eyebrow}</span>
          <h1>{title}</h1>
        </div>
      </div>

      {children}
    </>
  );
}

function FieldGallery() {
  const photos = [
    {
      url: "/media/relay-panel-lineup.jpeg",
      title: "132kV Switchyard — Gantry & Panel Work"
    },
    {
      url: "/posters/cb-mechanism.jpeg",
      title: "Circuit Breaker Erection & Fitting"
    },
    {
      url: "/posters/hydra-handling.jpeg",
      title: "Heavy Equipment Handling"
    },
    {
      url: "/posters/structural-welding.jpeg",
      title: "Structural Welding & Fabrication"
    }
  ];

  return (
    <section className="field-gallery">
      <div className="project-preview-heading">
        <SectionIntro
          eyebrow="05 / FROM THE FIELD"
          title={
            <>
              Real sites.
              <br />
              <em>Real work.</em>
            </>
          }
          copy="Photographs taken on our own substation, switchyard and panel assignments."
        />

        <Link className="arrow-link" to="/gallery">
          View full gallery <ArrowUpRight size={17} />
        </Link>
      </div>

      <div className="field-strip">
        {photos.map((p, i) => (
          <Link
            to="/gallery"
            className="field-photo"
            key={p.url}
          >
            <div
              style={{
                backgroundImage: `url(${p.url})`
              }}
            />

            <span>{p.title}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  const [category, setCategory] = useState("All");
  const [active, setActive] = useState(null);

  const media = [
    // IMAGES
    {
      id: 1,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "132kV Switchyard — Gantry, Bus & Insulator Work (UPPTCL)",
      url: "/media/relay-panel-lineup.jpeg"
    },
    {
      id: 2,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "Substation & Switchyard Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.29.jpeg"
    },
    {
      id: 3,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "Electrical Substation Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.30.jpeg"
    },
    {
      id: 4,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "Switchyard Site Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.30 (1).jpeg"
    },
    {
      id: 5,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "Switchyard Equipment Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.37.jpeg"
    },
    {
      id: 6,
      type: "image",
      category: "SUBSTATION & SWITCHYARD",
      title: "Electrical Infrastructure Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.37 (1).jpeg"
    },
    {
      id: 7,
      type: "image",
      category: "EQUIPMENT ERECTION",
      title: "Equipment Erection Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.56.jpeg"
    },
    {
      id: 8,
      type: "image",
      category: "EQUIPMENT ERECTION",
      title: "Circuit Breaker & Equipment Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.56 (1).jpeg"
    },
    {
      id: 9,
      type: "image",
      category: "CONTROL PANELS & WIRING",
      title: "Control Panel & Wiring Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.57.jpeg"
    },
    {
      id: 10,
      type: "image",
      category: "CONTROL PANELS & WIRING",
      title: "Electrical Panel Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.57 (1).jpeg"
    },
    {
      id: 11,
      type: "image",
      category: "CONTROL PANELS & WIRING",
      title: "Protection & Control Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.58.jpeg"
    },
    {
      id: 12,
      type: "image",
      category: "CONTROL PANELS & WIRING",
      title: "Transformer Control Panel",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.58 (1).jpeg"
    },
    {
      id: 13,
      type: "image",
      category: "STRUCTURAL & FABRICATION WORKS",
      title: "Structural & Fabrication Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.59.jpeg"
    },
    {
      id: 14,
      type: "image",
      category: "STRUCTURAL & FABRICATION WORKS",
      title: "Structural Fabrication",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.59 (1).jpeg"
    },
    {
      id: 15,
      type: "image",
      category: "STRUCTURAL & FABRICATION WORKS",
      title: "Fabrication & Welding Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.38.59 (2).jpeg"
    },
    {
      id: 16,
      type: "image",
      category: "HT/LT & CABLING WORKS",
      title: "HT/LT Electrical Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.39.00.jpeg"
    },
    {
      id: 17,
      type: "image",
      category: "HT/LT & CABLING WORKS",
      title: "Cable & Electrical Installation",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.48.jpeg"
    },
    {
      id: 18,
      type: "image",
      category: "HT/LT & CABLING WORKS",
      title: "Cabling Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.48 (1).jpeg"
    },
    {
      id: 19,
      type: "image",
      category: "EARTHING & SUPPLY MATERIALS",
      title: "Earthing & Supply Materials",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.49.jpeg"
    },
    {
      id: 20,
      type: "image",
      category: "EARTHING & SUPPLY MATERIALS",
      title: "Earthing Installation Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.50.jpeg"
    },
    {
      id: 21,
      type: "image",
      category: "SOLAR & LIGHTING SUPPLY",
      title: "Solar & Electrical Installation",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.51.jpeg"
    },
    {
      id: 22,
      type: "image",
      category: "SOLAR & LIGHTING SUPPLY",
      title: "Solar & Lighting Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.51 (1).jpeg"
    },
    {
      id: 23,
      type: "image",
      category: "SOLAR & LIGHTING SUPPLY",
      title: "Electrical Site Work",
      url: "/media/WhatsApp Image 2026-08-15 at 21.42.51 (2).jpeg"
    },

    // VIDEOS
    {
      id: 24,
      type: "video",
      category: "HT/LT & CABLING WORKS",
      title: "Cable Trench Work",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.36.mp4"
    },
    {
      id: 25,
      type: "video",
      category: "HT/LT & CABLING WORKS",
      title: "Electrical Cable Installation",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.39.mp4"
    },
    {
      id: 26,
      type: "video",
      category: "STRUCTURAL & FABRICATION WORKS",
      title: "Structural Welding & Fabrication",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.55.mp4"
    },
    {
      id: 27,
      type: "video",
      category: "STRUCTURAL & FABRICATION WORKS",
      title: "Structural Welding Work",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.55 (1).mp4"
    },
    {
      id: 28,
      type: "video",
      category: "HEAVY EQUIPMENT HANDLING",
      title: "Heavy Equipment Handling",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.56.mp4"
    },
    {
      id: 29,
      type: "video",
      category: "EQUIPMENT ERECTION",
      title: "Equipment Erection Work",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.57.mp4"
    },
    {
      id: 30,
      type: "video",
      category: "SUBSTATION & SWITCHYARD",
      title: "Substation & Switchyard Work",
      url: "/media/WhatsApp Video 2026-08-15 at 21.38.58.mp4"
    }
  ];

  const categories = [
    "All",
    "HT/LT & CABLING WORKS",
    "STRUCTURAL & FABRICATION WORKS",
    "HEAVY EQUIPMENT HANDLING",
    "EQUIPMENT ERECTION",
    "CONTROL PANELS & WIRING",
    "EARTHING & SUPPLY MATERIALS",
    "SUBSTATION & SWITCHYARD",
    "SOLAR & LIGHTING SUPPLY"
  ];

  const filtered =
    category === "All"
      ? media
      : media.filter((item) => item.category === category);

  return (
    <PageShell
      title={
        <>
          Our work, as it happens.
        </>
      }
      eyebrow="05 / FROM THE FIELD"
    >
      <section className="page-section" data-testid="gallery-page">

        <SectionIntro
          eyebrow="REAL SITES. REAL WORK."
          title="Our work, as it happens."
          copy="Substation bays, control panels, switchyard structures and live erection work — photographed and recorded on our own assignments."
        />

        <div
          className="filter-row"
          data-testid="gallery-category-filters"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              className={category === cat ? "selected" : ""}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div
          className="gallery-grid"
          data-testid="gallery-grid"
        >
          {filtered.map((item) => (
            <button
              type="button"
              className="gallery-item"
              key={item.id}
              onClick={() => setActive(item)}
            >
              <div
                className="gallery-image"
                style={{
                  backgroundImage:
                    item.type === "image"
                      ? `url("${item.url}")`
                      : "none"
                }}
              >
                {item.type === "video" && (
                  <video
                    src={item.url}
                    muted
                    playsInline
                    preload="metadata"
                    onMouseEnter={(e) => {
                      e.currentTarget.play().catch(() => {});
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.pause();
                      e.currentTarget.currentTime = 0;
                    }}
                  />
                )}

                {item.type === "video" && (
                  <span className="play-badge">
                    <Play size={22} fill="currentColor" />
                  </span>
                )}
              </div>

              <div className="gallery-caption">
                <span>
                  {item.type === "video"
                    ? `VIDEO • ${item.category}`
                    : item.category}
                </span>

                <h3>{item.title}</h3>
              </div>
            </button>
          ))}
        </div>

        {active && (
          <div
            className="lightbox"
            onClick={() => setActive(null)}
          >
            <button
              className="lightbox-close"
              onClick={() => setActive(null)}
              aria-label="Close"
            >
              <X size={24} />
            </button>

            <figure
              onClick={(e) => e.stopPropagation()}
            >
              {active.type === "video" ? (
                <video
                  src={active.url}
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={active.url}
                  alt={active.title}
                />
              )}

              <figcaption className="lightbox-caption">
                <span className="eyebrow gold">
                  {active.category}
                </span>

                <p>{active.title}</p>
              </figcaption>
            </figure>
          </div>
        )}

      </section>
    </PageShell>
  );
}
function About() {
  return (
    <PageShell
      title={
        <>
          Built for the demands
          <br />
          <em>of the field.</em>
        </>
      }
      eyebrow="ABOUT THE COMPANY"
    >
    <section className="page-section about-page">
  <div
    className="about-banner"
    style={{
      backgroundImage:
        "url(https://customer-assets-39nsmqrw.emergentagent.net/job_power-solutions-hub-17/artifacts/9eea0na1_WhatsApp%20Image%202026-08-15%20at%2021.42.51%20%282%29.jpeg)"
    }}
  >
          <span>
            M/S MAA VAISHNO CONSTRUCTION AND SUPPLIER
          </span>
        </div>

        <SectionIntro
          eyebrow="THE MAA VAISHNO APPROACH"
          title={
            <>
              Practical capability.
              <br />
              <em>Clear execution.</em>
            </>
          }
          copy="Our work sits where planning meets the realities of a live electrical site."
        />

        <div className="about-columns">
          <p>
            We support electrical and power infrastructure
            projects through equipment erection, installation,
            testing, maintenance and allied supply. Our
            capabilities extend across substations, HT/LT
            works, solar and lighting, specialized earthing,
            fabrication and heavy equipment handling.
          </p>

          <p>
            Every assignment is approached with attention to
            site conditions, safe movement of equipment and
            the details that turn a scope of work into
            dependable infrastructure.
          </p>
        </div>

        <div className="principles">
          <div>
            <span>01</span>
            <h3>Execution first</h3>
            <p>Site-ready support for complex electrical work.</p>
          </div>

          <div>
            <span>02</span>
            <h3>Connected capability</h3>
            <p>
              From procurement through installation and
              maintenance.
            </p>
          </div>

          <div>
            <span>03</span>
            <h3>Built to support</h3>
            <p>
              Responsive help when infrastructure needs it.
            </p>
          </div>
        </div>

        <div className="founder-section">
          <div className="founder-photo">
           <img
  src="https://customer-assets-39nsmqrw.emergentagent.net/job_power-solutions-hub-17/artifacts/pa6l3nm1_owner.jpeg"
  alt="Rajesh Kumar Awasthi — Founder & Proprietor"
/>
          </div>

          <div>
            <span className="eyebrow gold">
              LEADERSHIP
            </span>

            <h2>Rajesh Kumar Awasthi</h2>

            <p className="founder-title">
              FOUNDER & PROPRIETOR
            </p>

            <p className="founder-copy">
              “Every substation, every panel and every
              supply order carries our name. We treat client
              infrastructure the way we treat our own — with
              discipline, safety and accountability.”
            </p>

            <p className="founder-copy2">
              Rajesh Kumar Awasthi leads M/S Maa Vaishno
              Construction and Supplier as an A-Class
              Government Contractor — executing UPPTCL power
              transmission works, government and private
              tenders, solar projects and electrical equipment
              supply across Uttar Pradesh.
            </p>
          </div>
        </div>

        <CredentialsPanel />
      </section>
    </PageShell>
  );
}

function Services() {
  return (
    <PageShell
      title={
        <>
          Nine ways to move
          <br />
          <em>power forward.</em>
        </>
      }
      eyebrow="OUR CORE CAPABILITIES"
    >
      <section className="page-section">
        <SectionIntro
          eyebrow="01 — 09 / CAPABILITY INDEX"
          title="Industrial electrical work, without the guesswork."
          copy="Explore the disciplines we bring together for infrastructure, construction and supply."
        />

        <ServiceGrid />
      </section>
    </PageShell>
  );
}

function ServiceDetail() {
  const { slug } = useParams();

  const services = {
    "substation-bay-construction": {
      number: "01",
      title: "Substation & Bay Construction",
      headline: "done with discipline.",
      capability:
        "33kV / 132kV / 220kV Substation • Bay Construction • Equipment Erection • Installation • Commissioning",
      description:
        "Substation works, bay construction, equipment erection, installation and commissioning for demanding power infrastructure.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/91839781fd659c03c9517af509bbe5b8e846ad8f431dbca23e11646d8ef11f46.jpeg"
    },

    "ht-lt-infrastructure": {
      number: "02",
      title: "HT/LT Infrastructure",
      headline: "built for dependable distribution.",
      capability:
        "HT/LT Electrical Works • Cabling • Distribution Systems • Installation • Maintenance",
      description:
        "HT/LT electrical works, cabling, distribution systems, installation and maintenance.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/e76929e2b3178ce2a71a25f8bc6434795102f40e64633d0e46893cb4ad0c7069.jpeg"
    },

    "heavy-equipment-carriage-handling": {
      number: "03",
      title: "Heavy Equipment Carriage & Handling",
      headline: "moved with control.",
      capability:
        "Transformer • CT/PT • Circuit Breaker • Loading • Carriage • Unloading • Hydra/Crane Handling",
      description:
        "Safe loading, carriage, unloading, shifting and handling of heavy electrical equipment.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/3f25347acda5b232442ce5cb3594468cf36c32338f66307dc73bb16e4ab16573.jpeg"
    },

    "protection-control-panels-earthing": {
      number: "04",
      title: "Protection, Control Panels & Earthing",
      headline: "protection engineered right.",
      capability:
        "Control & Relay Panels • Protection Systems • Cable Termination • Interconnection • Equipment Earthing",
      description:
        "Protection systems, panels, cable termination, interconnection and specialized earthing.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/c622b50259edb57f4b639ad118171160c3b7d2cf8d576d4f537b9246689363b4.jpeg"
    },

    "tower-structural-fabrication-works": {
      number: "05",
      title: "Tower, Structural & Fabrication Works",
      headline: "built for the field.",
      capability:
        "Tower Works • Equipment Structures • Fabrication • Welding • Structural Installation • Foundation Modification",
      description:
        "Tower, structural, welding, fabrication and foundation modification works.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/86b34ace8ff4692bef96842b1acf54e80dfaf7b1a305d9b3c8a32d2ef5dce551.jpeg"
    },

    "transformer-switchgear-services": {
      number: "06",
      title: "Transformer & Switchgear Services",
      headline: "precision at every connection.",
      capability:
        "Transformer Installation • Switchgear • Circuit Breakers • Dismantling • Erection • Testing",
      description:
        "Transformer and switchgear installation, dismantling, erection and testing.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/40fa01f34819c8803afbd917ac7c083d6fb25213a57e777b145aa1f5074fa80b.jpeg"
    },

    "solar-lighting-solutions": {
      number: "07",
      title: "Solar & Lighting Solutions",
      headline: "clean power, practical lighting.",
      capability:
        "Solar Projects • Solar Installation • LED Lighting • Street Lighting • Electrical Lighting Solutions",
      description:
        "Solar projects, LED lighting, street lighting and electrical lighting solutions.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/c2b83d61a64329b47683bead4abeadfc2054ddce9289ddf255fe01fca9c919b1.jpeg"
    },

    "electrical-maintenance-emergency-services": {
      number: "08",
      title: "Electrical Maintenance & Emergency Services",
      headline: "support when infrastructure needs it.",
      capability:
        "Breakdown Support • Equipment Replacement • Testing • Repair • Preventive Maintenance • AC Maintenance",
      description:
        "Breakdown support, equipment replacement, testing, repair, preventive and AC maintenance.",
      image:
        "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1400&q=85"
    },

    "electrical-supply-procurement-tender-execution": {
      number: "09",
      title: "Electrical Supply, Procurement & Tender Execution",
      headline: "from supply to execution.",
      capability:
        "Electrical Equipment • Cables • Switchgear • Lighting & Earthing Materials • Solar Equipment • Tender Execution",
      description:
        "Supply and procurement of electrical equipment with end-to-end government and private tender execution.",
      image:
        "https://static.prod-images.emergentagent.com/jobs/4fd07d0b-5391-4fe5-bed7-c4fe316bb05a/images/bac4d731d52d8f7f03e7a2362e2f4c93ae25c440dc4898007f7e9961cdd5b201.jpeg"
    }
  };

  const service = services[slug];

  if (!service) {
    return (
      <PageShell
        title="Service not found"
        eyebrow="404 / SERVICES"
      >
        <div className="large-empty">
          <p>This capability is not available.</p>

          <Link
            to="/services"
            className="button gold-button"
          >
            Back to services
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={
        <>
          {service.title}
          <br />
          <em>{service.headline}</em>
        </>
      }
      eyebrow={`${service.number} / SERVICE DETAIL`}
    >
      <section className="page-section service-detail-page">

        <div className="service-detail-lead">

          <span className="service-number">
            {service.number}
          </span>

          <div
            className="service-detail-image"
            style={{
              backgroundImage: `url("${service.image}")`
            }}
          >
            <span>
              CAPABILITY / {service.number}
            </span>
          </div>

          <SectionIntro
            eyebrow="CAPABILITY PROFILE"
            title="A connected scope of work."
            copy={service.description}
          />

        </div>

        <div className="service-capability-panel">

          <span className="eyebrow gold">
            CAPABILITY LINE
          </span>

          <h2>
            {service.capability}
          </h2>

          <p>
            Talk to our team about site requirements,
            equipment, execution planning or supply support
            for this capability.
          </p>

          <div className="hero-actions">

            <Link
              to="/quote"
              className="button gold-button"
            >
              Request a Quote
              <ArrowUpRight size={17} />
            </Link>

            <Link
              to="/contact"
              className="text-button"
            >
              Contact the team
              <ChevronRight size={17} />
            </Link>

          </div>

        </div>

      </section>
    </PageShell>
  );
}

function Projects() {
  const [projects, setProjects] = useState([]);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((r) => setProjects(r.data.data || []))
      .catch(() => setProjects([]));
  }, []);

  const filtered = useMemo(
    () =>
      category === "All"
        ? projects
        : projects.filter(
            (p) => p.category === category
          ),
    [category, projects]
  );

  return (
    <PageShell
      title={
        <>
          Work that holds
          <br />
          <em>its ground.</em>
        </>
      }
      eyebrow="OUR WORK / EXPERIENCE"
    >
      <section className="page-section">
        <div className="filter-row">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              className={
                category === c ? "selected" : ""
              }
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length ? (
          <div className="project-grid">
            {filtered.map((p) => (
              <Link
                to={`/projects/${p.slug}`}
                className="project-card"
                key={p.slug}
              >
                <div
                  className="project-card-image"
                  style={{
                    backgroundImage: `url(${
                      p.images?.[0] ||
                      PROJECT_IMAGE
                    })`
                  }}
                />

                <div>
                  <span>{p.category}</span>
                  <h3>{p.title}</h3>
                  <p>{p.shortDescription}</p>
                  <ArrowUpRight size={18} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="large-empty">
            <FileText size={27} />

            <h3>
              10+ years of electrical execution experience.
            </h3>

            <p>
              For over a decade our team has delivered
              substation, power transmission, solar and
              electrical supply works across Uttar Pradesh.
            </p>

            <Link
              to="/quote"
              className="button gold-button"
            >
              Start an inquiry <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
      </section>
    </PageShell>
  );
}

function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects/${slug}`)
      .then((r) => setProject(r.data.data))
      .catch(() => setProject(false));
  }, [slug]);

  if (project === false) {
    return (
      <PageShell
        title="Project not found"
        eyebrow="404 / OUR WORK"
      >
        <div className="large-empty">
          <p>This project is not available publicly.</p>

          <Link
            to="/projects"
            className="button gold-button"
          >
            Back to our work
          </Link>
        </div>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <div className="loading-state">
        Loading project details…
      </div>
    );
  }

  return (
    <PageShell
      title={project.title}
      eyebrow={project.category}
    >
      <section className="page-section project-detail">
        <img
          src={project.images?.[0] || PROJECT_IMAGE}
          alt={project.title}
        />

        <div className="project-meta">
          <span>
            {project.location ||
              "Location available on request"}
          </span>

          <span>
            {project.year ||
              "Project year available on request"}
          </span>
        </div>

        <SectionIntro
          eyebrow="PROJECT OVERVIEW"
          title="The scope of work."
          copy={
            project.description ||
            project.shortDescription
          }
        />

        <div className="scope-grid">
          {(project.scope || []).map((item, i) => (
            <div key={item}>
              <span>0{i + 1}</span>
              <p>{item}</p>
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}

function FormPage({ type }) {
  const isQuote = type === "quote";
  const isTender = type === "tender";

  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const form = new FormData(e.currentTarget);
    const data = Object.fromEntries(form.entries());

    if (data.attachment instanceof File) {
      data.attachment = data.attachment.name;
    }

    try {
      await axios.post(
        `${API}/${
          type === "contact" ? "contact" : `${type}s`
        }`,
        data
      );

      setSent(true);
      e.currentTarget.reset();
    } catch (err) {
      const detail = err.response?.data?.detail;

      const message = Array.isArray(detail)
        ? detail
            .map(
              (item) =>
                item.msg ||
                item.message ||
                "Please check this field."
            )
            .join(" ")
        : typeof detail === "object" && detail
        ? detail.message
        : detail;

      setError(
        message ||
          "Please check the form and try again."
      );
    }
  };

  const fields = isQuote
    ? [
        ["name", "Name", true],
        ["company", "Company", false],
        ["phone", "Phone", true],
        ["email", "Email", false],
        ["location", "Location", false],
        ["service", "Service required", true],
        ["projectType", "Project type", false],
        [
          "message",
          "Tell us about the requirement",
          true
        ]
      ]
    : isTender
    ? [
        ["name", "Name", true],
        ["company", "Company", false],
        ["email", "Email", true],
        ["phone", "Phone", true],
        [
          "tenderProjectName",
          "Tender / Project name",
          true
        ],
        ["tenderReference", "Tender reference", false],
        [
          "department",
          "Department / Organization",
          false
        ],
        [
          "submissionDeadline",
          "Submission deadline",
          false
        ],
        ["message", "Tender notes", true]
      ]
    : [
        ["name", "Name", true],
        ["phone", "Phone", true],
        ["email", "Email", false],
        ["message", "How can we help?", true]
      ];

  return (
    <PageShell
      title={
        isQuote ? (
          <>
            Let’s discuss your
            <br />
            <em>next requirement.</em>
          </>
        ) : isTender ? (
          <>
            Bring us into
            <br />
            <em>the conversation.</em>
          </>
        ) : (
          <>
            A direct line to
            <br />
            <em>our team.</em>
          </>
        )
      }
      eyebrow={
        isQuote
          ? "QUOTE INQUIRY"
          : isTender
          ? "TENDER INQUIRY"
          : "CONTACT"
      }
    >
      <section className="page-section form-layout">
        <div>
          <SectionIntro
            eyebrow="START A CONVERSATION"
            title={
              isQuote
                ? "Tell us what needs building."
                : isTender
                ? "Share the opportunity."
                : "We’re ready to listen."
            }
            copy="Complete the form and our team will get back to you using the details provided."
          />

          <div className="direct-contact">
            <a href={`tel:${contact.phone}`}>
              <Phone size={16} />
              Call +91 {contact.phone}
            </a>

            <a
              href={`https://wa.me/${contact.phone2}?text=Hello%20Maa%20Vaishno%2C%20I%20would%20like%20to%20discuss%20an%20electrical%20requirement.`}
              target="_blank"
              rel="noreferrer"
            >
              <Send size={16} />
              WhatsApp us
            </a>

            <a href={`mailto:${contact.email}`}>
              <Mail size={16} />
              Email us
            </a>
          </div>
        </div>

        {sent ? (
          <div className="success-box">
            <Check size={30} />

            <h3>
              Thank you. Your inquiry has been received.
            </h3>

            <p>
              Our team will review the details and connect
              with you shortly.
            </p>

            <button
              className="button gold-button"
              onClick={() => setSent(false)}
            >
              Send another inquiry
            </button>
          </div>
        ) : (
          <form
            className="inquiry-form"
            onSubmit={submit}
          >
            {fields.map(
              ([name, label, required]) => (
                <label key={name}>
                  {label}
                  {required && <sup>*</sup>}

                  {name === "message" ? (
                    <textarea
                      name={name}
                      required={required}
                      rows="5"
                      placeholder=" "
                    />
                  ) : (
                    <input
                      name={name}
                      type={
                        name === "email"
                          ? "email"
                          : "text"
                      }
                      required={required}
                      placeholder=" "
                    />
                  )}
                </label>
              )
            )}

            {(isQuote || isTender) && (
              <label>
                Attachment
                <input
                  name="attachment"
                  type="file"
                />
              </label>
            )}

            <input
              className="honeypot"
              name="website"
              tabIndex="-1"
              autoComplete="off"
            />

            <button
              className="button gold-button"
              type="submit"
            >
              Submit inquiry <ArrowUpRight size={17} />
            </button>

            {error && (
              <p className="form-error">
                {error}
              </p>
            )}
          </form>
        )}
      </section>
    </PageShell>
  );
}

function WhatsAppFloat() {
  return (
    <a
      className="whatsapp-float"
      href={`https://wa.me/91${contact.phone2}?text=Hello%20Maa%20Vaishno%2C%20I%20would%20like%20to%20discuss%20an%20electrical%20requirement.`}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat on WhatsApp"
    >
      <svg
        viewBox="0 0 32 32"
        width="26"
        height="26"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.59-1.67a12.74 12.74 0 0 0 6.21 1.6h.01c7.06 0 12.79-5.74 12.79-12.8 0-3.42-1.33-6.63-3.75-9.05a12.72 12.72 0 0 0-9.05-3.68zm0 23.36h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4.02 1.02 1.07-3.92-.25-.4a10.55 10.55 0 0 1-1.63-5.65c0-5.87 4.78-10.64 10.65-10.64 2.84 0 5.51 1.11 7.52 3.12a10.57 10.57 0 0 1 3.11 7.53c0 5.87-4.78 10.65-10.65 10.65zm5.84-7.97c-.32-.16-1.89-.93-2.19-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55h-.61c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.14 3.08 1.3 3.29.16.21 2.25 3.44 5.45 4.82.76.33 1.36.53 1.82.67.77.24 1.46.21 2.01.13.61-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
      </svg>
    </a>
  );
}

function Contact() {
  return <FormPage type="contact" />;
}

function App() {
  return (
    <BrowserRouter>
      <SeoManager />

      <Header />

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route
            path="/services/:slug"
            element={<ServiceDetail />}
          />
          <Route path="/projects" element={<Projects />} />
          <Route
            path="/projects/:slug"
            element={<ProjectDetail />}
          />
          <Route path="/gallery" element={<Gallery />} />
          <Route
            path="/quote"
            element={<FormPage type="quote" />}
          />
          <Route
            path="/tender"
            element={<FormPage type="tender" />}
          />
          <Route path="/contact" element={<Contact />} />

          <Route
            path="*"
            element={
              <PageShell
                title="Page not found"
                eyebrow="404"
              >
                <div className="large-empty">
                  <Link
                    to="/"
                    className="button gold-button"
                  >
                    Return home
                  </Link>
                </div>
              </PageShell>
            }
          />
        </Routes>
      </main>

      <Footer />
      <WhatsAppFloat />
    </BrowserRouter>
  );
}

export default App;
